import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { localText } from '@/lib/localText';
import logo from '@/assets/logo.webp';

const Footer = () => {
  const { lang, t } = useLanguage();
  const lt = (en: string, ar: string, fr?: string) => localText(lang, en, ar, fr);

  return (
    <footer className="bg-charcoal text-primary-foreground">
      {/* Gold accent line */}
      <div className="h-px gold-gradient-bg" />
      
      <div className="container py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <img src={logo} alt="A&M Gold Mining Fund" className="h-10 w-10 rounded-full ring-1 ring-secondary/30" />
              <span className="text-primary-foreground font-display text-lg tracking-wide">
                {t.nav.brandName}
              </span>
            </div>
            <p className="text-primary-foreground/55 text-sm leading-relaxed max-w-xs">
              {t.footer.description}
            </p>
          </div>

          {/* Fund Information */}
          <div className="lg:col-span-2">
            <p className="font-display text-secondary text-xs uppercase tracking-[0.2em] mb-6">{lt('Fund', 'الصندوق', 'Fonds')}</p>
            <ul className="space-y-3 text-sm text-primary-foreground/55">
              <li><Link to={`/${lang}/about`} className="hover:text-secondary transition-colors duration-300">{t.nav.about}</Link></li>
              <li><Link to={`/${lang}/strategy`} className="hover:text-secondary transition-colors duration-300">{t.nav.strategy}</Link></li>
              <li><Link to={`/${lang}/projects`} className="hover:text-secondary transition-colors duration-300">{t.nav.projects}</Link></li>
              <li><Link to={`/${lang}/investor`} className="hover:text-secondary transition-colors duration-300">{t.nav.investor}</Link></li>
            </ul>
          </div>

          {/* Regulatory */}
          <div className="lg:col-span-2">
            <p className="font-display text-secondary text-xs uppercase tracking-[0.2em] mb-6">{lt('Regulatory', 'التنظيم', 'Réglementaire')}</p>
            <ul className="space-y-3 text-sm text-primary-foreground/55">
              <li><Link to={`/${lang}/governance`} className="hover:text-secondary transition-colors duration-300">{t.nav.governance}</Link></li>
              <li className="text-primary-foreground/50">{lang === 'ar' ? <>ترخيص هيئة السوق المالية رقم <span dir="ltr">37-07058</span></> : lt('CMA License No. 37-07058', '', 'Licence CMA n° 37-07058')}</li>
              <li className="text-primary-foreground/50">{lang === 'ar' ? <>شهادة الشريعة: <span dir="ltr">IC-1552</span></> : lt('Shariah Cert: IC-1552', '', 'Cert. Charia : IC-1552')}</li>
            </ul>
          </div>

          {/* Legal */}
          <div className="lg:col-span-2">
            <p className="font-display text-secondary text-xs uppercase tracking-[0.2em] mb-6">{lt('Legal', 'قانوني', 'Juridique')}</p>
            <ul className="space-y-3 text-sm text-primary-foreground/55">
              <li><Link to={`/${lang}/legal`} className="hover:text-secondary transition-colors duration-300">{t.nav.legalDisclaimer}</Link></li>
              <li><Link to={`/${lang}/privacy`} className="hover:text-secondary transition-colors duration-300">{t.nav.privacyPolicy}</Link></li>
              <li><Link to={`/${lang}/terms`} className="hover:text-secondary transition-colors duration-300">{t.nav.termsOfUse}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2">
            <p className="font-display text-secondary text-xs uppercase tracking-[0.2em] mb-6">{lt('Contact', 'تواصل', 'Contact')}</p>
            <ul className="space-y-3 text-sm text-primary-foreground/55">
              <li><Link to={`/${lang}/contact`} className="hover:text-secondary transition-colors duration-300">{t.nav.contact}</Link></li>
              <li className="text-primary-foreground/50">{lt('Saudi Arabia', 'المملكة العربية السعودية', 'Arabie Saoudite')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Risk Disclaimer */}
      <div className="border-t border-primary-foreground/5">
        <div className="container py-8">
          <p className="text-[11px] text-primary-foreground/50 leading-relaxed max-w-4xl">
            {t.footer.disclaimer}
          </p>
          <p className="text-[11px] text-primary-foreground/50 leading-relaxed mt-3 font-medium">
            {t.footer.riskWarning}
          </p>
        </div>
      </div>

      <div className="border-t border-primary-foreground/5">
        <div className="container py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-primary-foreground/50">
            {t.footer.copyright}
          </span>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-primary-foreground/50 tracking-[0.15em] uppercase">
              {lt('CMA Regulated', 'منظم من هيئة السوق المالية', 'Réglementé CMA')}
            </span>
            <span className="text-secondary text-[11px]">•</span>
            <span className="text-[11px] text-primary-foreground/50 tracking-[0.15em] uppercase">
              {lt('KPMG Audited', 'مدقق من KPMG', 'Audité par KPMG')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
